import cookie from 'js-cookie'
import jwtdecode from 'jwt-decode'
import React, { useContext, useEffect, useState } from 'react'
import {
  COOKIE_AVATAR,
  COOKIE_FAVOURITES,
  COOKIE_FAVOURITES_IDS,
  COOKIE_FULL_NAME,
  COOKIE_INITIALS,
  COOKIE_JWT,
  COOKIE_PREFERENCES_DEPARTURE_AIRPORTS,
  COOKIE_PREFERENCES_TRAVELPARTY,
  COOKIE_TUI_USER_ID,
} from './application/constants'
import { addScript } from './billy'
import { apiKey, language, urls } from './resources'

const context = React.createContext()

const useAuth = () => {
  const data = useContext(context)
  if (!data) {
    throw new Error(`useAuth must be used within an AuthProvider.`)
  }
  return data
}

const clearCookies = () => {
  const favouriteCount = (cookie.get(COOKIE_FAVOURITES_IDS) || '').split(',').filter((p) => p).length

  //cookie.remove(COOKIE_AUTHENTICATION)
  cookie.remove(COOKIE_JWT)
  cookie.remove(COOKIE_FULL_NAME)
  cookie.remove(COOKIE_TUI_USER_ID)
  cookie.set(COOKIE_FAVOURITES, favouriteCount)
  cookie.remove(COOKIE_AVATAR)
  cookie.remove(COOKIE_INITIALS)
  cookie.remove(COOKIE_PREFERENCES_DEPARTURE_AIRPORTS)
  cookie.remove(COOKIE_PREFERENCES_TRAVELPARTY)
  // Clear authentication cache for Internet Explorer
  document.execCommand('ClearAuthenticationCache')
  window.mytui.topmenu && window.mytui.topmenu()
}

const decodeToken = (token) => {
  try {
    const decoded = jwtdecode(token)
    return {
      ...decoded,
      firstName: decoded.firstName,
      lastName: decoded.lastName,
    }
  } catch (e) {
    return null
  }
}

const initialData = {
  initialized: false,
  isLoggedIn: false,
  authmethod: null,
  profile: {
    id: null,
    admin: null,
    firstName: null,
    lastName: null,
    email: null,
    picture: null,
  },
}

const isTokenValid = (token) => {
  const decoded = decodeToken(token)
  if (!decoded) return false

  const expireTime = new Date(0).setUTCSeconds(decoded.exp)
  var currentTime = new Date().getTime()
  return expireTime - currentTime > 5000
}

const waitForGigyaReady = () => {
  return new Promise((resolve) => {
    if (window.gigya && window.gigya.hasSession) {
      resolve()
      return
    }

    const existingCallback = window.onGigyaServiceReady
    window.onGigyaServiceReady = () => {
      if (existingCallback) existingCallback()
      resolve()
    }
  })
}

const AuthProvider = ({ ...props }) => {
  const [data, setData] = useState(initialData)
  const [accessToken, setAccessToken] = useState(null)
  const [isReady, setIsReady] = useState(false)
  let ongoingFetch = null

  useEffect(() => {
    const initialize = async () => {
      if (!window.gigya || !window.gigya.accounts) {
        const lang = { se: 'sv', dk: 'da', fi: 'fi', no: 'no' }[language]
        addScript('https://cdns.gigya.com/js/gigya.js?apikey=' + apiKey + '&lang=' + lang)
      }

      await waitForGigyaReady()

      setData({
        ...data,
        initialized: true,
        isLoggedIn: await window.gigya.hasSession(),
      })
    }

    if (!data.initialized) {
      initialize()
    }
  }, [])

  useEffect(() => {
    const initiate = async () => {
      const token = await setToken()

      if (token) {
        const decoded = decodeToken(token)
        if (decoded) {
          setData({
            ...data,
            authmethod: decoded.authmethod,
            profile: {
              id: decoded.sub,
              admin: decoded.admin,
              firstName: decoded.firstName,
              lastName: decoded.lastName,
              email: decoded.email,
              picture: decoded.picture || decoded.photoURL,
            },
          })
        }
      }
      setIsReady(true)
    }
    if (data.initialized) {
      initiate()
    }
  }, [data.initialized])

  const getAuthToken = async () => {
    if (ongoingFetch) return ongoingFetch

    if (!window.gigya || !window.gigya.accounts) return null

    ongoingFetch = new Promise((resolve) => {
      try {
        window.gigya.accounts.getJWT({
          fields: 'firstName,lastName,email,photoURL',
          callback: (jwt) => {
            if (jwt.status === 'OK') {
              resolve(jwt.id_token)
            } else {
              resolve(null)
            }
          },
        })
      } finally {
        ongoingFetch = null
      }
    })
    return ongoingFetch
  }

  const setToken = async () => {
    const token = await getAuthToken()
    setAccessToken(token)
    cookie.set(COOKIE_JWT, token, { expires: 720 })
    return token
  }

  const getAccessToken = async () => {
    if (!accessToken) return await setToken()

    if (isTokenValid(accessToken)) return accessToken

    return await setToken()
  }

  const handleChangeName = (firstName, lastName) => {
    // TODO
  }

  const localLogout = () => {
    clearCookies()
    setData({ isLoggedIn: false })

    if (window.location.pathname !== urls.signIn) window.location = urls.signIn
  }

  const handleLogout = () => {
    if (window.gigya && window.gigya.accounts) window.gigya.accounts.logout()
  }

  const actions = {
    logout: () => handleLogout(),
    changeName: (firstName, lastName) => handleChangeName(firstName, lastName),
    getAccessToken: () => getAccessToken(),
  }

  const authData = {
    ...data,
    actions,
  }

  const gigyaLoadInterval = setInterval(() => {
    if (window.gigya && window.gigya.accounts) {
      clearInterval(gigyaLoadInterval)
      window.gigya.accounts.addEventHandlers({
        onLogout: () => localLogout(),
      })
    } else {
    }
  }, 10)

  if (!isReady) return null

  return <context.Provider value={authData} {...props} />
}

export { AuthProvider, useAuth }
